import { useCallback, useEffect, useRef, useState } from 'react'

// 2 × (60 px button + 24 px outer margin) — voir backwardBtnProps / forwardBtnProps.
const NAV_BUTTONS_WIDTH = 2 * (60 + 24)
const TESTIMONIAL_CARD_MAX_WIDTH = 325
const TESTIMONIAL_CARD_SLIDE_GAP = 8

export function useCarouselMount(enabled = true) {
    const shellRef = useRef<HTMLDivElement>(null)
    const [containerWidth, setContainerWidth] = useState(0)
    const [isLayoutStable, setIsLayoutStable] = useState(false)

    useEffect(() => {
        if (!enabled) {
            setContainerWidth(0)
            setIsLayoutStable(false)
            return
        }

        const el = shellRef.current
        if (!el) return

        const update = () => {
            const width = el.offsetWidth
            if (width > 0) setContainerWidth(width)
        }

        update()
        const observer = new ResizeObserver(update)
        observer.observe(el)
        return () => observer.disconnect()
    }, [enabled])

    // react-simply-carousel démarre avec window width = 0 ; on attend la
    // stabilisation de son layout interne avant de révéler le carousel.
    useEffect(() => {
        if (containerWidth <= 0) {
            setIsLayoutStable(false)
            return
        }

        let cancelled = false
        const timeoutId = window.setTimeout(() => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (!cancelled) setIsLayoutStable(true)
                })
            })
        }, 80)

        return () => {
            cancelled = true
            window.clearTimeout(timeoutId)
        }
    }, [containerWidth])

    const markLayoutStable = useCallback(() => {
        setIsLayoutStable(true)
    }, [])

    return {
        shellRef,
        containerWidth,
        canMount: containerWidth > 0,
        isLayoutStable,
        markLayoutStable,
    }
}

export function getCarouselShellClassName(isLayoutStable: boolean) {
    return `w-full min-w-0 overflow-hidden transition-opacity duration-150 ${
        isLayoutStable ? 'opacity-100' : 'opacity-0'
    }`
}

export const carouselContainerProps = {
    className: 'w-full min-w-0',
    style: {
        width: '100%',
        minWidth: 0,
        userSelect: 'none' as const,
    },
}

export const carouselInnerProps = {
    className: 'min-w-0 w-full overflow-hidden',
    style: { minWidth: 0, width: '100%', overflow: 'hidden' },
}

export function getFullWidthSlideStyle(
    containerWidth: number,
    navButtonsWidth = NAV_BUTTONS_WIDTH,
    maxSlideWidth = 960
) {
    const width = Math.min(
        Math.max(containerWidth - navButtonsWidth, 320),
        maxSlideWidth
    )
    return {
        width,
        minWidth: width,
        maxWidth: width,
        flex: `0 0 ${width}px`,
    } as const
}

/**
 * `react-simply-carousel` calcule la maxWidth de son viewport interne comme
 * `Σ slide.offsetWidth` sans tenir compte du gap flex du items list — d'où
 * une dernière slide rognée. On rend donc le slot edge-to-edge (gap = 0 sur
 * le items list) et on transporte l'espace visuel via un paddingRight
 * interne au slot ; la carte visible occupe 100 % du content-box.
 */
export function getResponsiveSlideStyle(
    containerWidth: number,
    itemsToShow: number,
    {
        navButtonsWidth = NAV_BUTTONS_WIDTH,
        gap = TESTIMONIAL_CARD_SLIDE_GAP,
        maxCardWidth = TESTIMONIAL_CARD_MAX_WIDTH,
    }: {
        navButtonsWidth?: number
        gap?: number
        maxCardWidth?: number
    } = {}
) {
    const safeItems = Math.max(itemsToShow, 1)
    const available = Math.max(containerWidth - navButtonsWidth, 0)
    const interCardGap = safeItems > 1 ? gap : 0
    const maxSlotWidth = maxCardWidth + interCardGap
    const slotWidth = Math.max(
        0,
        Math.min(Math.floor(available / safeItems), maxSlotWidth)
    )
    return {
        width: slotWidth,
        minWidth: slotWidth,
        maxWidth: slotWidth,
        flex: `0 0 ${slotWidth}px`,
        paddingRight: interCardGap,
        boxSizing: 'border-box',
    } as const
}
